import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpenCheck,
  Calculator,
  CheckCircle2,
  CircleDot,
  Database,
  HeartHandshake,
  Layers3,
  Ruler,
  Search,
  ShieldCheck,
  Target,
  Wrench,
} from 'lucide-react';

import usePageMetadata from '../hooks/usePageMetadata';
import MaterialToolCards from './MaterialDatabase/MaterialToolCards';
import './Home.css';

const HOME_TITLE = 'CNC Machining Tools, Materials & Practical Data | ASiJoLab';
const HOME_DESCRIPTION =
  'Practical CNC machining knowledge and tools for materials, dimensional tolerances, fits and Metric M thread calculations.';

const benefits = [
  {
    title: 'Find faster',
    text: 'Stop searching for the same technical information across scattered websites, catalogs and datasheets.',
    Icon: Search,
  },
  {
    title: 'Understand better',
    text: 'Move beyond raw numbers with machining context that helps explain what the data means in practice.',
    Icon: BookOpenCheck,
  },
  {
    title: 'Decide better',
    text: 'Use structured data, comparisons and practical context to make clearer technical decisions.',
    Icon: Target,
  },
  {
    title: 'Stay focused',
    text: 'Work in a clean environment without ads or unnecessary elements pulling attention away from the task.',
    Icon: ShieldCheck,
  },
];

const productAreas = [
  {
    title: 'Materials',
    headline: 'Understand what you are machining.',
    text: 'Use practical material properties and comparisons to understand how an engineering material may behave in real machining work.',
    capabilities: ['Tensile strength', 'Thermal conductivity', 'Elongation', 'Chemical composition', 'Hardness comparison'],
    cta: 'Explore Materials',
    to: '/materials',
    Icon: Database,
  },
  {
    title: 'Tolerances',
    headline: 'Know what size is acceptable — and how to verify it.',
    text: 'Determine dimensional limits, evaluate or select fits, and calculate DIN 7162 GO/NO-GO limit-gauge dimensions.',
    capabilities: ['Limit Deviations', 'Fit Calculator', 'Fit Finder', 'Recommended Fits', 'DIN 7162 Gauge Calculator'],
    cta: 'Explore Tolerances',
    to: '/tolerances',
    Icon: Ruler,
  },
  {
    title: 'Threads',
    headline: 'Calculate Metric M thread limits.',
    text: 'Explore listed and custom Metric M threads, backend-supported tolerance classes and calculated dimensional limits.',
    capabilities: ['Internal threads', 'External threads', 'Listed sizes and pitches', 'Custom metric threads', 'Tolerance limits'],
    cta: 'Explore Threads',
    to: '/threads',
    Icon: CircleDot,
  },
];

const nextSteps = [
  {
    title: 'Richer material information',
    text: 'More useful material context around properties, equivalents and machining relevance.',
    Icon: Layers3,
  },
  {
    title: 'Machining data',
    text: 'Cutting conditions and practical data connected to real CNC work.',
    Icon: Wrench,
  },
  {
    title: 'Calculators',
    text: 'Focused tools for everyday decisions at the machine and in preparation.',
    Icon: Calculator,
  },
  {
    title: 'Practical knowledge',
    text: 'Machining notes and technical explanations that support better decisions.',
    Icon: Ruler,
  },
];

const IconBadge = ({ Icon }) => (
  <span className="homeIconBadge" aria-hidden="true">
    <Icon size={21} strokeWidth={1.9} />
  </span>
);

const Home = () => {
  usePageMetadata(HOME_TITLE, HOME_DESCRIPTION);

  return (
    <div className="homePage">
      <section className="homeHero" aria-labelledby="home-hero-title">
        <div className="homeHeroCopy">
          <p className="homeEyebrow">CNC machining workspace</p>
          <h1 id="home-hero-title">
            Practical CNC machining knowledge and tools for real decisions.
          </h1>
          <p className="homeHeroText">
            ASiJoLab is a practical workspace for CNC machining, bringing
            trusted technical information, machining context and useful tools
            for materials, dimensional tolerances and Metric M threads together in one place.
          </p>
          <div className="homeHeroActions">
            <a className="homeButton homeButtonPrimary" href="#product-areas">
              Explore ASiJoLab
              <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
            </a>
            <a className="homeButton homeButtonSecondary" href="#why-asijolab">
              Why it exists
            </a>
          </div>
        </div>

        <div className="homeHeroVisual" aria-hidden="true">
          <div className="homeVisualHeader">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="homeVisualGrid">
            <div className="homeVisualPanel">
              <Database size={30} strokeWidth={1.8} />
              <span>Materials</span>
            </div>
            <div className="homeVisualPanel">
              <Ruler size={30} strokeWidth={1.8} />
              <span>Tolerances</span>
            </div>
            <div className="homeVisualPanel">
              <CircleDot size={30} strokeWidth={1.8} />
              <span>Threads</span>
            </div>
          </div>
          <div className="homeVisualRows">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section>

      <section className="homeSection homeProductSection" id="product-areas" aria-labelledby="product-areas-title">
        <div className="homeSectionHeader">
          <p className="homeAccentText">Explore ASiJoLab</p>
          <h2 id="product-areas-title">Three practical areas for machining work.</h2>
        </div>
        <div className="homeProductGrid">
          {productAreas.map(({ title, headline, text, capabilities, cta, to, Icon }) => (
            <article className="homeProductArea" key={title}>
              <header>
                <IconBadge Icon={Icon} />
                <p>{title}</p>
                <h3>{headline}</h3>
              </header>
              <p>{text}</p>
              <ul>
                {capabilities.map((capability) => <li key={capability}>{capability}</li>)}
              </ul>
              <Link className="homeButton homeButtonPrimary" to={to}>
                {cta}
                <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
      </section>

      <div className="homeSection">
        <MaterialToolCards headingId="home-material-tools-title" />
      </div>

      <section
        className="homeSection homeBenefitsSection"
        aria-labelledby="benefits-title"
      >
        <div className="homeSectionHeader">
          <p className="homeAccentText">What ASiJoLab gives you</p>
          <h2 id="benefits-title">
            Find faster. Understand better. Decide better. Stay focused.
          </h2>
        </div>
        <div className="homeBenefitGrid">
          {benefits.map(({ title, text, Icon }) => (
            <article className="homeBenefitCard" key={title}>
              <IconBadge Icon={Icon} />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="homeSection homeIntroSection"
        aria-labelledby="what-is-asijolab-title"
      >
        <div className="homeSectionHeader">
          <p className="homeAccentText">What is ASiJoLab?</p>
          <h2 id="what-is-asijolab-title">
            More than a material database.
          </h2>
        </div>
        <div className="homeIntroLayout">
          <p>
            The material database is an important foundation of ASiJoLab today,
            but the project is not limited to storing material tables. Its goal
            is to connect reliable data with the context machinists need when
            working with real parts, real tools and real machines.
          </p>
          <div className="homeTrustNote">
            <CheckCircle2 size={22} strokeWidth={1.9} aria-hidden="true" />
            <p>
              Technical information is selected and checked manually. ASiJoLab
              does not automatically generate and publish technical content with
              AI.
            </p>
          </div>
        </div>
      </section>

      <section
        className="homeSection homeWhySection"
        id="why-asijolab"
        aria-labelledby="why-asijolab-title"
      >
        <div className="homeSectionHeader">
          <p className="homeAccentText">Why ASiJoLab exists</p>
          <h2 id="why-asijolab-title">
            Built from a practical problem in daily work.
          </h2>
        </div>
        <div className="homeWhyLayout">
          <p>
            In machining, the same useful information often has to be found
            again and again. It is spread across websites, catalogs, datasheets
            and old saved links. A helpful technical website can disappear, stop
            working or become difficult to use, and the search starts again.
          </p>
          <p>
            ASiJoLab was created as one structured place for information that is
            genuinely useful during machining. If that information is reliable
            enough for real work and for sharing with other machinists, it can
            also help people working with CNC machining anywhere in the world.
          </p>
        </div>
      </section>

      <section
        className="homeSection homeNextSection"
        aria-labelledby="next-title"
      >
        <div className="homeSectionHeader">
          <p className="homeAccentText">What's next</p>
          <h2 id="next-title">
            ASiJoLab continues to grow around practical machining work.
          </h2>
        </div>
        <div className="homeProgression" aria-label="ASiJoLab development direction">
          {nextSteps.map(({ title, text, Icon }) => (
            <article className="homeProgressStep" key={title}>
              <IconBadge Icon={Icon} />
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        className="homeSupportSection"
        aria-labelledby="support-title"
      >
        <div className="homeSupportCopy">
          <p className="homeAccentText">Support ASiJoLab</p>
          <h2 id="support-title">A clean workspace should stay clean.</h2>
          <p>
            ASiJoLab is designed without ads, so technical work stays focused
            and free from advertising noise. Supporter and Supporter Plus are
            voluntary ways to help cover operation and continue development.
          </p>
        </div>
        <Link className="homeButton homeButtonPrimary" to="/support">
          Support ASiJoLab
          <HeartHandshake size={18} strokeWidth={2} aria-hidden="true" />
        </Link>
      </section>

    </div>
  );
};

export default Home;
